import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  Country,
  GridDrawerComponent,
  InvestmentJurisdiction,
} from '../../models';

@Component({
  standalone: false,
  selector: 'gstar-upsert-investment-jurisdiction',
  templateUrl: './upsert-investment-jurisdiction.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpsertInvestmentJurisdictionComponent
  extends GridDrawerComponent<InvestmentJurisdiction>
  implements OnInit, OnChanges
{
  private readonly fb = inject(FormBuilder);

  public form: FormGroup = this.fb.group({
    country: ['', Validators.required],
    region: ['', Validators.required],
    isActive: true,
    isDisplay: true,
  });
  enableUpdate: boolean = false;

  destroyRef = inject(DestroyRef);

  get countryValue(): Country {
    return this.form.get('country')?.value;
  }

  get countryField(): AbstractControl<Country> {
    return this.form.get('country')!;
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: InvestmentJurisdiction) => {
        if (this.isEdit) this.checkChanges(values);
        this.modelChange.emit({
          model: {
            ...this.model,
            ...values,
          },
          prevModel: this.model!,
          isValid:
            this.isEdit && this.form.valid
              ? this.enableUpdate
              : this.form.valid,
        });
      });

    // Load form values once country list has been loaded
    this.loadFormValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && !changes['model'].firstChange) {
      this.enableUpdate = false;
      this.loadFormValues();
    }
  }

  loadFormValues(): void {
    if (!this.model) return;
    const currentValue = this.model as InvestmentJurisdiction;
    const selectedRegion = currentValue.country?.region ?? null;

    this.form.setValue({
      country: currentValue.country ? { ...currentValue.country } : null,
      region: selectedRegion ?? null,
      isActive: currentValue?.isActive,
      isDisplay: currentValue?.isDisplay,
    });
  }

  countrySelect(country: Country): void {
    this.form.patchValue({ country, region: country.region });
  }

  countryClear(): void {
    this.form.patchValue({ country: null, region: null });
  }

  private checkChanges(values: InvestmentJurisdiction): void {
    if (values && this.model) {
      this.enableUpdate =
        values.isActive !== this.model.isActive ||
        values.isDisplay !== this.model.isDisplay;
    }
  }
}
